import dayjs from 'dayjs';

import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const renderdate = (date, format = 'MMM DD, YYYY hh:mm A') => {
  const parsed = dayjs.utc(date, 'YYYY-MM-DD HH:mm:ss').local();
  return parsed.isValid() ? parsed.format(format) : '';
};

export const formatDateRange = (startDate, endDate) => {
  return `${startDate.getMonth() + 1}_${startDate.getDate()}_${startDate.getFullYear()}-${endDate.getMonth() +
    1}_${endDate.getDate()}_${endDate.getFullYear()}`;
};

export const addMinutes = (timeString, minutesToAdd, timeFormat = 'h:mm A') => {
  const baseTime = dayjs(`1970-01-01 ${timeString}`, 'YYYY-MM-DD h:mm A');
  const updatedTime = baseTime.add(minutesToAdd, 'minute');
  return updatedTime.isValid() ? updatedTime.format(timeFormat) : '';
};

export const compareDatetimeStrings = (datetimeA, datetimeB) => {
  if (datetimeA < datetimeB) {
    return -1;
  }

  if (datetimeA > datetimeB) {
    return 1;
  }

  return 0;
};

export const compareTimeStrings = (timeA, timeB) => {
  const dateA = new Date(`1970/01/01 ${timeA}`);
  const dateB = new Date(`1970/01/01 ${timeB}`);
  return compareDatetimeStrings(dateA, dateB);
};

export const isTimeStringInBetween = (time, compareA, compareB) => {
  return compareTimeStrings(time, compareA) === 1 && compareTimeStrings(time, compareB) === -1;
};

export const getYesterdayDate = () => {
  const date = new Date();
  const yesterDay = new Date(date.getTime());
  yesterDay.setDate(date.getDate() - 1);
  return yesterDay;
};

export const getTodayDate = () => {
  const date = new Date();
  return new Date(date.getTime());
};

export const compareDayjsDates = (dateADayjs, dateBDayjs) => {
  if (dateADayjs.isBefore(dateBDayjs)) {
    return -1;
  }

  if (dateADayjs.isAfter(dateBDayjs)) {
    return 1;
  }

  return 0;
};

export const compareDates = (dateA, dateB) => {
  const dateADayjs = dayjs(dateA).startOf('day');
  const dateBDayjs = dayjs(dateB).startOf('day');

  if (dateADayjs.isBefore(dateBDayjs)) {
    return -1;
  }

  if (dateADayjs.isAfter(dateBDayjs)) {
    return 1;
  }

  return 0;
};

export const subtractDate = (date, numOfUnits = 1, unit = 'day') => {
  const dateDayjs = dayjs(date).subtract(numOfUnits, unit);
  return dateDayjs.toDate();
};

export const renderFirestoreDate = (firestoreTimestamp, dateTimezone, outputFormat = 'MMM DD, YYYY hh:mm A') => {
  if (!firestoreTimestamp) {
    return '';
  }

  const dateUTCDayjs = dayjs.utc(firestoreTimestamp.toDate());

  const dateDayjs = dateTimezone ? dateUTCDayjs.tz(dateTimezone) : dateUTCDayjs.local();

  if (!dateDayjs.isValid()) {
    return '';
  }

  return dateDayjs.format(outputFormat);
};

export const firestoreTimestampToDayjs = (firestoreTimestamp, dateTimezone) => {
  if (!firestoreTimestamp) {
    return null;
  }

  const dateUTCDayjs = dayjs.utc(firestoreTimestamp.toDate());

  const dateDayjs = dateTimezone ? dateUTCDayjs.tz(dateTimezone) : dateUTCDayjs.local();

  if (!dateDayjs.isValid()) {
    return null;
  }

  return dateDayjs;
};

export const renderUnixEpoch = (unixEpoch, renderTimezone, format = 'MMM DD, YYYY hh:mm A', defaultValue = '') => {
  if (!unixEpoch || !renderTimezone) {
    return defaultValue;
  }

  const dateTimeObj = dayjs.unix(unixEpoch).tz(renderTimezone);

  if (!dateTimeObj.isValid()) {
    return defaultValue;
  }

  return dateTimeObj.format(format);
};

export const getMonthDayYearFormat = (dateString) => {
  const [month, day, year] = dateString.split('/');
  const monthFormat = month.length === 1 ? 'M' : 'MM';
  const dayFormat = day.length === 1 ? 'D' : 'DD';
  const yearFormat = year.length === 2 ? 'YY' : 'YYYY';
  return `${monthFormat}/${dayFormat}/${yearFormat}`;
};

export const stringToDayjsNullInvalid = (str) => {
  const dateDayjs = dayjs(str);

  if (!dateDayjs.isValid()) {
    return null;
  }

  return dateDayjs;
};

export const getLaterDate = (dateA, dateB) => {
  const dateADayjs = dayjs(dateA).startOf('day');
  const dateBDayjs = dayjs(dateB).startOf('day');

  if (dateADayjs.isBefore(dateBDayjs)) {
    return dateB;
  }

  if (dateADayjs.isAfter(dateBDayjs)) {
    return dateA;
  }

  return dateB;
};

export const getSunday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  return d;
};

export const getSaturday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() + (6 - day));
  return d;
};

export const checkSameWeek = (dateA, dateB) => {
  const dateADayjs = dayjs(dateA).startOf('week');
  const dateBDayjs = dayjs(dateB).startOf('week');
  return dateADayjs.isSame(dateBDayjs);
};

export const getStartOfMonth = (date) => {
  const d = new Date(date);
  d.setDate(1);
  return d;
};

export const getEndOfMonth = (date) => {
  const d = new Date(date);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  d.setDate(lastDay.getDate());
  return d;
};

export const getQuarterYear = (date) => {
  const d = new Date(date);
  const month = d.getMonth();
  const quarter = Math.floor(month / 3) + 1;
  const year = d.getFullYear();
  return { quarter, year };
};

export const getDateFromQuarterYear = (year, quarter) => {
  const startMonth = (quarter - 1) * 3;
  const startDate = new Date(year, startMonth, 1);
  const endDate = new Date(year, startMonth + 3, 0);
  return { startDate, endDate };
};

export const getMonthIndexFromQuarter = (quarter, isStartIndex = true) => {
  const monthMap = {
    1: isStartIndex ? 0 : 2,
    2: isStartIndex ? 3 : 5,
    3: isStartIndex ? 6 : 8,
    4: isStartIndex ? 9 : 11,
  };

  return monthMap[quarter];
};

export const getLastDateOfMonthIndex = (year, monthIndex) => {
  return new Date(year, monthIndex + 1, 0).getDate();
};

export const getStartOfQuarter = (date) => {
  const d = new Date(date);
  const month = d.getMonth();
  const quarter = Math.floor(month / 3) * 3;
  return new Date(d.getFullYear(), quarter, 1);
};

export const getEndOfQuarter = (date) => {
  const d = new Date(date);
  const month = d.getMonth();
  const quarter = Math.floor(month / 3) * 3 + 2; // end month of the quarter (0-based)
  return new Date(d.getFullYear(), quarter + 1, 0); // day 0 of next month gives last day of quarter
};

export const getStartOfYear = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), 0, 1);
};

export const getEndOfYear = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), 11, 31);
};
